import type { NotificationFeedItem } from "@gobro/contracts";
import type { CustomerApi } from "./api";

export type WebPushSubscriptionResult = "denied" | "disabled" | "subscribed" | "unsupported";

export async function subscribeWebPush(api: CustomerApi): Promise<WebPushSubscriptionResult> {
  if (
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";
  const key = await api.notificationPushKey();
  if (!key.enabled || !key.publicKey) return "disabled";
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    applicationServerKey: urlBase64ToUint8Array(key.publicKey),
    userVisibleOnly: true,
  });
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.auth || !json.keys.p256dh) return "unsupported";
  await api.registerNotificationDevice({
    platform: "web_push",
    subscription: {
      endpoint: json.endpoint,
      keys: { auth: json.keys.auth, p256dh: json.keys.p256dh },
    },
    userAgent: navigator.userAgent.slice(0, 300),
  });
  return "subscribed";
}

export function connectNotificationStream(
  api: CustomerApi,
  onNotification: (notification: NotificationFeedItem) => void,
): () => void {
  let closed = false;
  let socket: WebSocket | null = null;
  void api
    .notificationRealtimeTicket()
    .then(({ ticket }) => {
      if (closed) return;
      const url = new URL(`${api.baseUrl}/notifications/ws`, window.location.href);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(url);
      socket.onopen = () =>
        socket?.send(JSON.stringify({ data: { ticket }, event: "notifications.authenticate.v1" }));
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as {
            data?: { notification?: NotificationFeedItem };
            event?: string;
          };
          if (message.event === "notification.created.v1" && message.data?.notification) {
            onNotification(message.data.notification);
          }
        } catch {
          // Ignore malformed realtime frames; the periodic feed poll stays authoritative.
        }
      };
    })
    .catch(() => undefined);
  return () => {
    closed = true;
    socket?.close();
  };
}

function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
}
